import { Component, OnInit, computed, input, model } from '@angular/core';
import type { LinkValue } from '@front-platform/content-builder';

import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { ModalsService } from '@core/services/modals/modals.service';
import { LinkNavigationService } from '@core/services/link-navigation/link-navigation.service';
import { FidelityType } from '@shared/modals/kiosk-fidelity-registration/fidelity-type.enum';
import { ZerosixRegistrationStep } from './zerosix-registration-step.enum';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { ColorFieldTypesValues } from '@config/values/color-field-types.values';
import { ContentBuilderFieldColors } from '@shared/interfaces/content-builder.interface';
import { ZerosixService } from '@core/services/zerosix/zerosix.service';
import { take } from 'rxjs';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { resolvePhoneDefaultRegionCode } from '@common/utils/phone-default-region.util';
import { AnalyticsService } from '@core/services/app/analytics.service';

@Component({
  selector: 'app-zerosix-registration-modal',
  templateUrl: './zerosix-registration-modal.component.html',
  styles: [
    `
      /* Allow the country list to overflow the modal on small screens */
      :host {
        @media (max-width: 767px) {
          /* Main modal container */
          .fixed.left-0.right-0 {
            overflow: visible !important;
          }

          /* White modal panel */
          .rounded-t-3xl {
            overflow: visible !important;
          }
        }
      }
    `,
  ],
  standalone: false,
})
export class ZerosixRegistrationModalComponent extends DestroyerBase implements OnInit {
  static handle = 'zerosix-registration-modal';
  keyboardOffset = 0; // For keyboard-aware positioning

  phone = model<string>('');
  readonly goToURLOnClose = input<string | LinkValue>();
  readonly goToKioskRegister = input<boolean | undefined>(false);
  registrationStep = ZerosixRegistrationStep;
  readonly activeStep = model(this.registrationStep.ZEROSIX_STEP_PHONE);

  readonly backgroundColor = input<IContentBuilderFieldColor>();

  readonly actionFontColor = input<IContentBuilderFieldColor | string>();

  readonly actionBackgroundColor = input<IContentBuilderFieldColor | string>();

  readonly actionFieldColor = computed<ContentBuilderFieldColors | undefined>(() => {
    const font = this.actionFontColor();
    const background = this.actionBackgroundColor();
    if (!font && !background) {
      return undefined;
    }
    return {
      font: this.getFormattedFontColor(font),
      background: this.getFormattedBackgroundColor(background),
    };
  });

  protected readonly defaultRegionCode = computed(() =>
    resolvePhoneDefaultRegionCode(
      undefined,
      this.marketplaceStoreSelector.selectMarketplaceCountrySignal(),
    ),
  );

  public readonly FidelityType = FidelityType;

  constructor(
    private readonly modalsService: ModalsService,
    private readonly linkNavigation: LinkNavigationService,
    private readonly zerosixService: ZerosixService,
    private readonly marketplaceStoreSelector: MarketplaceStoreSelector,
    private readonly analyticsService: AnalyticsService,
  ) {
    super();
  }

  private getFormattedFontColor(font: string | IContentBuilderFieldColor | undefined) {
    if (typeof font === 'string') {
      return font.startsWith('#')
        ? {
            type: ColorFieldTypesValues.Hex,
            value: font,
          }
        : {
            type: ColorFieldTypesValues.Palette,
            value: font,
          };
    }
    return font;
  }

  private getFormattedBackgroundColor(background: string | IContentBuilderFieldColor | undefined) {
    if (typeof background === 'string') {
      return background.startsWith('#')
        ? {
            type: ColorFieldTypesValues.Hex,
            value: background,
          }
        : {
            type: ColorFieldTypesValues.Palette,
            value: background,
          };
    }
    return background;
  }

  ngOnInit() {
    if (this.goToKioskRegister()) {
      this.onGoToRegisterStep();
    }
  }

  onClose(): void {
    this.modalsService.close(ZerosixRegistrationModalComponent.handle);
  }

  onComplete(): void {
    this.onClose();
    const goToURLOnClose = this.goToURLOnClose();
    if (goToURLOnClose) {
      this.linkNavigation.navigate(goToURLOnClose);
    }
  }

  onGoToOtpStep(): void {
    this.zerosixService
      .isZerosixOtpIdentificationRequired$()
      .pipe(take(1))
      .subscribe(isOtpRequired => {
        if (isOtpRequired) {
          this.activeStep.set(this.registrationStep.ZEROSIX_STEP_OTP);
        } else {
          this.onGoToConfirmationStep();
        }
      });
  }

  setPhone(phone: string): void {
    this.phone.set(phone);
    this.activeStep.set(this.registrationStep.ZEROSIX_STEP_ACCOUNT_NOT_FOUND);
  }

  onGoToAccountNotFoundStep() {
    this.activeStep.set(this.registrationStep.ZEROSIX_STEP_ACCOUNT_NOT_FOUND);
  }

  onGoToPhoneStep() {
    this.activeStep.set(this.registrationStep.ZEROSIX_STEP_PHONE);
  }

  onGoToAuthenticationStep() {
    this.activeStep.set(this.registrationStep.ZEROSIX_STEP_AUTHENTICATION);
  }

  onGoToConfirmationStep() {
    this.activeStep.set(this.registrationStep.ZEROSIX_STEP_KIOSK_CONFIRMATION);
  }

  onGoToRegisterStep() {
    this.activeStep.set(this.registrationStep.ZEROSIX_STEP_KIOSK_REGISTER);
  }

  onGoToKioskRegisterConfirmationStep() {
    this.activeStep.set(this.registrationStep.ZEROSIX_STEP_KIOSK_REGISTER_CONFIRMATION);
  }

  onGoToKioskRegistrationSuccessStep() {
    this.activeStep.set(this.registrationStep.ZEROSIX_STEP_KIOSK_REGISTRATION_SUCCESS);
  }

  onGoToKioskAccountNotFoundStep() {
    this.activeStep.set(this.registrationStep.ZEROSIX_STEP_KIOSK_ACCOUNT_NOT_FOUND);
  }
}
